import React, { useMemo } from 'react';
import { X, MapPin, User, Truck, AlertCircle, MapPinOff } from 'lucide-react';

function DeliveriesList({ deliveries, onClose, onDeliveryClick }) {
  
  // Split deliveries into those with and without location
  const { withLocation, withoutLocation } = useMemo(() => {
    const with_loc = deliveries.filter(d => d.latitude && d.longitude);
    const without_loc = deliveries.filter(d => !d.latitude || !d.longitude);
    
    // Sort by sequence (if exists), then by slot (for pending), then by id
    const sortBySequence = (a, b) => {
      if (a.sequence && b.sequence) {
        return a.sequence - b.sequence;
      }
      if (a.sequence) return -1;
      if (b.sequence) return 1;
      
      // If both pending, sort by slot
      if (a.status === 'pending' && b.status === 'pending') {
        return (a.slot || 999) - (b.slot || 999);
      }
      
      return a.id - b.id;
    };
    
    return {
      withLocation: with_loc.sort(sortBySequence),
      withoutLocation: without_loc.sort(sortBySequence)
    };
  }, [deliveries]);

  function getStatusBadge(status) {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      case 'encamino':
        return 'bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      case 'entregado':
        return 'bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      case 'cancelado':
        return 'bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      case 'rechazado':
        return 'bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      default:
        return 'bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-semibold';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En Camino',
      entregado: 'Entregado',
      cancelado: 'Cancelado',
      rechazado: 'Rechazado',
    };
    return labels[status] || status;
  }

  function getSlotColor(slot) {
    switch (slot) {
      case 1:
        return 'bg-[#BF360C]';
      case 2:
        return 'bg-[#E65100]';
      case 3:
        return 'bg-[#F57C00]';
      case 4:
        return 'bg-[#FFB74D]';
      case 5:
        return 'bg-[#FFE0B2]';
      default:
        return 'bg-slate-400';
    }
  }

  function DeliveryItem({ delivery, showNoLocation = false }) {
    return (
      <button
        onClick={() => onDeliveryClick(delivery)}
        className="w-full bg-white rounded-lg p-3 shadow-sm border border-slate-200 hover:shadow-md hover:border-[#B1336E]/30 transition-all active:scale-[0.98]"
      >
        <div className="flex items-start gap-3">
          {/* Sequence or Slot Indicator */}
          <div className="flex-shrink-0">
            {delivery.sequence ? (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm font-montserrat">
                  {delivery.sequence}
                </span>
              </div>
            ) : delivery.status === 'pending' && delivery.slot ? (
              <div className={`w-10 h-10 rounded-full ${getSlotColor(delivery.slot)} flex items-center justify-center shadow-sm`}>
                <span className="text-white font-bold text-xs font-montserrat">
                  S{delivery.slot}
                </span>
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                <Truck className="w-5 h-5 text-slate-500" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 text-left">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-sm text-slate-900 font-montserrat truncate">
                {delivery.recipient_name || 'Sin nombre'}
              </h3>
              <span className={getStatusBadge(delivery.status)}>
                {getStatusLabel(delivery.status)}
              </span>
            </div>
            
            {delivery.address && (
              <p className="text-xs text-slate-600 font-montserrat truncate mb-1">
                <MapPin className="w-3 h-3 inline mr-1" />
                {delivery.address}
              </p>
            )}
            
            {delivery.district && (
              <p className="text-xs text-slate-500 font-montserrat">
                {delivery.district}
              </p>
            )}

            {showNoLocation && (
              <div className="flex items-center gap-1 mt-2 text-xs text-red-600 font-montserrat">
                <MapPinOff className="w-3 h-3" />
                <span className="font-semibold">Sin ubicación</span>
              </div>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-3 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#B1336E]" />
            <div>
              <h2 className="text-lg font-extrabold text-[#B1336E] font-montserrat">
                Lista de Entregas
              </h2>
              <p className="text-xs text-slate-600 font-montserrat">
                Total: {deliveries.length}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {deliveries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Truck className="w-16 h-16 text-slate-300 mb-3" />
              <p className="text-slate-600 font-montserrat font-semibold">
                No hay entregas disponibles
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Deliveries with Location */}
              {withLocation.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-[#B1336E]" />
                    <h3 className="text-sm font-bold text-slate-900 font-montserrat">
                      Con Ubicación ({withLocation.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {withLocation.map((delivery) => (
                      <DeliveryItem key={delivery.id} delivery={delivery} />
                    ))}
                  </div>
                </div>
              )}

              {/* Deliveries without Location */}
              {withoutLocation.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3 pt-2 border-t border-slate-200">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <h3 className="text-sm font-bold text-red-600 font-montserrat">
                      Sin Ubicación ({withoutLocation.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {withoutLocation.map((delivery) => (
                      <DeliveryItem 
                        key={delivery.id} 
                        delivery={delivery}
                        showNoLocation={true}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with Legend */}
        <div className="border-t border-slate-200 p-4">
          <p className="text-xs text-slate-600 font-montserrat mb-2 font-semibold">
            Leyenda de Urgencia:
          </p>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#BF360C]"></div>
              <span className="text-xs font-montserrat">S1-S2</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#F57C00]"></div>
              <span className="text-xs font-montserrat">S3</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#FFB74D]"></div>
              <span className="text-xs font-montserrat">S4-S5</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveriesList;